import { Component, OnInit, OnDestroy, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';
import { Button } from 'primeng/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientRecordsService } from '../../../core/services/client-records.service';

import { ContactWithRole } from '../../interfaces/contact.interface';
import { ContactResponseDto } from '@qc/shared';
import {
    AddContactToOrganizationDialogComponent,
    ContactToOrganizationData,
} from '../dialogs/add-contact-to-organization-dialog.component';

@Component({
    selector: 'qc-organization-contacts-manager',
    standalone: true,
    imports: [CommonModule, Button],
    template: `
        <div>
            <div class="flex justify-between items-center mb-3">
                <label class="block text-sm mb-1">Contacts</label>
                <p-button
                    severity="secondary"
                    variant="outlined"
                    size="small"
                    icon="pi pi-plus"
                    label="Add Contact"
                    (click)="showAddContactDialog()"
                />
            </div>

            <div *ngIf="contacts().length > 0" class="space-y-2">
                <div
                    *ngFor="let contact of contacts()"
                    class="flex items-center justify-between p-3 bg-gray-50 border border-gray-100"
                >
                    <div class="flex-1">
                        <div class="font-medium text-gray-900">{{ contact.fullName }}</div>
                        <div class="text-sm text-gray-500">{{ contact.email }}</div>
                        <div *ngIf="contact.roleDescription" class="text-sm text-blue-600">
                            {{ contact.roleDescription }}
                        </div>
                        <div *ngIf="contact.context" class="text-sm opacity-50">
                            {{ contact.context }}
                        </div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <p-button
                            severity="secondary"
                            variant="text"
                            size="small"
                            icon="pi pi-pencil"
                            (click)="editContact(contact)"
                            title="Edit contact"
                        />
                        <p-button
                            severity="danger"
                            variant="text"
                            size="small"
                            icon="pi pi-trash"
                            (click)="removeContact(contact.id)"
                            title="Remove contact"
                        />
                    </div>
                </div>
            </div>

            <div
                *ngIf="contacts().length === 0"
                class="text-center py-8 text-gray-500 border border-gray-200 bg-gray-50 rounded-lg"
            >
                No contacts assigned to this organization yet.
            </div>
        </div>
    `,
})
export class OrganizationContactsManagerComponent implements OnInit, OnDestroy {
    private clientRecordsService = inject(ClientRecordsService);
    private dialog = inject(Dialog);
    private destroy$ = new Subject<void>();

    organizationId = input<string>();
    contacts = input<ContactWithRole[]>([]);
    isEditMode = input<boolean>(false);

    contactsUpdated = output<void>();
    contactAdded = output<ContactWithRole>();
    contactRemoved = output<string>();

    ngOnInit() {
        // Component initialization
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    showAddContactDialog() {
        this.openContactDialog();
    }

    editContact(contact: ContactWithRole) {
        this.openContactDialog(contact);
    }

    private openContactDialog(contactToEdit?: ContactWithRole) {
        const dialogRef = this.dialog.open(AddContactToOrganizationDialogComponent);
        const componentInstance = dialogRef.componentInstance! as AddContactToOrganizationDialogComponent;

        if (contactToEdit) {
            componentInstance.setEditMode(contactToEdit);
        }

        componentInstance.contactAdded.subscribe((contactData: ContactToOrganizationData) => {
            const contactWithRole: ContactWithRole = {
                ...contactData.contact,
                roleDescription: contactData.roleDescription,
                context: contactData.context,
            };

            this.contactAdded.emit(contactWithRole);
        });
    }

    removeContact(contactId: string) {
        this.contactRemoved.emit(contactId);
    }
}
